import { useEffect, useRef, type ReactNode } from "react";

/**
 * A modal dialog, on the browser's own <dialog> opened with showModal().
 *
 * WHY NATIVE. The three dialogs were a div with role="dialog": focus stayed on the page behind, Tab walked
 * out of the dialog into controls hidden under the backdrop, and only one of them closed on Escape. A modal
 * <dialog> gets all of that from the browser: focus moves in, the rest of the page is inert (unreachable by
 * Tab, click and screen reader), Escape closes it, and it sits above everything with its own backdrop.
 *
 * Closing is still React's decision. Escape arrives as the dialog's `cancel` event, which is prevented and
 * turned into onClose, so the parent's state stays the single source of whether the dialog is shown.
 *
 * Focus goes back to whatever was focused before it opened (usually the button that opened it), so a keyboard
 * user carries on from where they were rather than from the top of the page.
 */
export function Modal({
  label,
  onClose,
  wide = false,
  children
}: {
  /** The dialog's accessible name, e.g. "Edit dish". */
  label: string;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // showModal is missing only in old browsers and in jsdom; open it plainly there.
    if (!dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    return () => {
      if (typeof dialog.close === "function" && dialog.open) dialog.close();
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={wide ? "modal-dialog wide" : "modal-dialog"}
      aria-label={label}
      onCancel={(e) => {
        e.preventDefault();
        close.current();
      }}
      // The dialog element is only a frame around the panel, so a click on it is a click on the backdrop.
      onClick={(e) => {
        if (e.target === e.currentTarget) close.current();
      }}
    >
      <div className={wide ? "modal wide" : "modal"}>{children}</div>
    </dialog>
  );
}
